import { useState } from "react"
import { Sparkles, ChevronDown, ChevronUp, Loader2, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { TextWithCitations } from "@/components/drafting/CitationBadge"
import type { CitationSource } from "@/components/drafting/CitationBadge"
import { usePreviewStore } from "@/stores/preview-store"
import type { AiScoringSource } from "@/stores/strategy-store"

interface AiRationaleBlockProps {
  rationale: string
  suggestedScore: number | null
  confidence: 'high' | 'medium' | 'low' | null
  /** The user's own score, compared with the AI's to show "matches" or offer "Use N". */
  currentScore?: number | null
  scoringSources?: AiScoringSource[] | null | undefined
  isLoading?: boolean
  onApplyScore?: (score: number) => void
}

function toCitationSources(sources: AiScoringSource[]): CitationSource[] {
  return sources.map(s => ({
    documentId: s.documentId,
    documentName: s.documentName,
    pageNumber: s.pageNumber,
  }))
}

// Low confidence is a caution, not an error — amber, never red.
const CONFIDENCE_CLASS: Record<'high' | 'medium' | 'low', string> = {
  high: "bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))]",
  medium: "bg-background text-muted-foreground",
  low: "bg-[hsl(var(--status-warning)/0.12)] text-[hsl(var(--status-warning))]",
}

export function AiRationaleBlock({ rationale, suggestedScore, confidence, currentScore = null, scoringSources, isLoading = false, onApplyScore }: AiRationaleBlockProps) {
  const [expanded, setExpanded] = useState(false)
  // Collapsed-bar summary: the AI's score is readable without opening the
  // reasoning. Reasoning itself stays collapsed until the user asks for it.
  const aiScore = isLoading ? null : suggestedScore
  const matchesUserScore = aiScore != null && currentScore === aiScore

  const citationSources = scoringSources ? toCitationSources(scoringSources) : []

  const handleNavigateToSource = (source: CitationSource) => {
    void usePreviewStore.getState().openPreview({
      documentId: source.documentId,
      documentName: source.documentName,
      ...(source.pageNumber != null && { page: source.pageNumber }),
      context: 'library',
      citationLabel: `Source: ${source.documentName}`,
    })
  }

  return (
    <div className="bg-muted/50 border border-border rounded-lg overflow-hidden">
      {/* Toggle and "Use N" are SIBLINGS. The apply action used to be a
          role="button" span nested inside the toggle, which is invalid HTML (a
          <button> permits no interactive descendants) and needed
          stopPropagation to work at all. As a sibling, applying never expands. */}
      <div className="w-full flex items-center justify-between px-3 py-2 text-xs gap-2 hover:bg-muted/80">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-1.5 font-medium text-[hsl(var(--ai-accent-text))] text-left"
        >
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
          AI assessment
          {isLoading && (
            <span className="inline-flex items-center gap-1 text-[10px] font-normal text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Scoring…
            </span>
          )}
          {aiScore != null && (
            <span className="font-normal text-foreground tabular-nums">
              · suggests {aiScore}
            </span>
          )}
          {!isLoading && confidence && (
            <span
              data-confidence={confidence}
              className={cn("px-1.5 py-0.5 rounded text-[10px] font-normal", CONFIDENCE_CLASS[confidence])}
            >
              Confidence: {confidence}
            </span>
          )}
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          {matchesUserScore && (
            <span className="text-meta inline-flex items-center gap-1">
              <Check className="h-3.5 w-3.5 text-[hsl(var(--status-success))]" aria-hidden="true" />
              matches your score
            </span>
          )}
          {aiScore != null && !matchesUserScore && onApplyScore && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Use AI score ${aiScore} out of 5`}
              onClick={() => onApplyScore(aiScore)}
              className="h-6 px-2"
            >
              Use {aiScore}
            </Button>
          )}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-muted-foreground"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 text-xs text-foreground leading-relaxed">
          {citationSources.length > 0 ? (
            <TextWithCitations
              text={rationale}
              sources={citationSources}
              onNavigateToSource={handleNavigateToSource}
            />
          ) : (
            rationale
          )}
        </div>
      )}
    </div>
  )
}
